import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { AuthLayout, PageSpinner, TextLink } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { confirmApplication } from '../../lib/developer-applications';

type State =
  | { status: 'loading' }
  | { status: 'done'; name: string }
  | { status: 'failed'; message: string };

/** Landing page of the "Confirm your MOGZO developer application" email. */
export function ConfirmDeveloperApplication() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    if (!token) {
      setState({ status: 'failed', message: 'This link is missing its code.' });
      return;
    }
    let active = true;
    // Confirming twice is harmless, so React's double effect in development is fine.
    confirmApplication(token)
      .then(({ name }) => active && setState({ status: 'done', name }))
      .catch((error) => {
        if (!active) return;
        setState({
          status: 'failed',
          message:
            error instanceof ApiError && error.code === 'invalid_token'
              ? error.message
              : 'We couldn’t confirm it just now. Try the link again in a moment.',
        });
      });
    return () => {
      active = false;
    };
  }, [token]);

  if (state.status === 'loading') return <PageSpinner />;
  if (state.status === 'failed') {
    return (
      <AuthLayout title="This link didn’t work">
        <p className="text-ink">{state.message}</p>
        <p className="mt-4 text-sm text-muted">
          <TextLink to="/developers/apply">Go to the application form</TextLink>
        </p>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout title="Application confirmed">
      <p className="text-ink">
        Thanks, {state.name.split(/\s+/)[0]}. Your application is in our review queue. We review
        every one by hand and will email you, usually within a few days.
      </p>
      <p className="mt-4 text-sm text-muted">
        <TextLink to="/">Back to MOGZO</TextLink>
      </p>
    </AuthLayout>
  );
}
